import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'antd';
import { openNotification } from '../../../utils';
import { useDispatch, useSelector } from 'react-redux';
import { calendarActions } from '../../../redux/actions/calendarActions';
import {
  calendarExceptionDayService,
  calendarService,
  ganttService,
  workingdayService
} from '../../../services';
import FormDays from '../CalendarForm/FormDays';
import FormExceptionsDays from '../CalendarForm/FormExceptionsDays';
import { withTranslation } from 'react-i18next';
import Editable from '../CalendarForm/FormExceptionsDays/Editable';
import { AMPLITUDE_SERVICE } from '../../../analytics/constants';
import { trackingEvent } from '../../../analytics/index';
import { getBasicAmplitudEventProperties } from '../../../analytics/utils';
import { activeSectorFlag } from '../../../utils/activeSectorFlag';
import CheckboxSystem from '../../DesignSystem/CheckboxSystem/CheckboxSystem';
import notificationSystemV2 from '../../DesignSystem/NotificationSystemV2';
import TooltipSystemV2 from '../../DesignSystem/TooltipSystemV2';
import {
  hasUndefinedShiftsHours,
  hasNoShifts,
  hasNoWorkingDaysSelected,
  hasMismatchingWorkingHours,
  DAYS_PER_WEEK
} from '../utils/shiftValidations';
import ShiftWarningModal from '../ShiftWarningModal';
import { trackCalendarEditEvents } from './utils';
import PropTypes from 'prop-types';

function CalendarEdit(props) {
  const { t, permission, sector } = props;
  const { handleCancelEdit, formatShift, add1Hour, loadCalendars } = props;

  const calendarState = useSelector((state) => state.calendarState);
  const projectState = useSelector((state) => state.projectState);
  const calendarsSelected = calendarState.calendarSelected;
  const dispatch = useDispatch();
  const inputRef = useRef();

  const createBaseArray = (length = DAYS_PER_WEEK) => Array(length).fill(false);
  const templateCalendar = {
    name: null,
    days: createBaseArray(),
    shiftStart_ini: createBaseArray(),
    shiftEnd_ini: createBaseArray(),
    shiftStart_end: createBaseArray(),
    shiftEnd_end: createBaseArray(),
    exceptions: [],
    is_default: true
  };

  const [loading, setLoading] = useState(false);
  const [isShiftsActive, setIsShiftsActive] = useState(false);
  const [currentGantt, setCurrentGantt] = useState();
  const [currentCalendar, setCurrentCalendar] = useState(templateCalendar);
  const [toDropWorkingDays, setToDropWorkingDays] = useState([]);
  const [showShiftWarningModal, setShowShiftWarningModal] = useState(false);

  useEffect(() => {
    loadGantt();
    if (calendarsSelected) {
      trackingEvent(
        'calendar_selection',
        {
          ...getBasicAmplitudEventProperties(),
          calendar_name: calendarsSelected?.name,
          calendar_id: calendarsSelected?.id
        },
        AMPLITUDE_SERVICE
      );
    }
  }, []);

  const getGantt = async () => {
    const gannt = await ganttService.showBySector(projectState.sectorSelected);
    return gannt;
  };

  const loadGantt = async () => {
    const gantt = await getGantt();
    if (gantt.gantt?.id) {
      setCurrentGantt(gantt.gantt.id);
    }
  };

  const createExceptionDay = async (data) => {
    const excCreated = await calendarExceptionDayService.create(data);
    return excCreated;
  };

  const deleteException = async (id) => {
    await calendarExceptionDayService.destroy(id);
  };

  const saveExceptions = async (calendarSelected) => {
    calendarState.calendarSelected.exceptions.map(async (e) => {
      await deleteException(e.id);
    });
    calendarState.calendarForm.exceptions.map(async (exc) => {
      const data = {
        name: exc.name ? exc.name : 'name',
        unique_id: new Date().getTime(),
        to_date: exc.to_date,
        from_date: exc.from_date,
        calendarId: calendarSelected.id,
        workable: exc.workable,
        iterable: exc.iterable
      };
      const start = `${isNaN(exc.hourini) ? null : formatShift(exc.hourini)}-${isNaN(exc.hourend) ? null : formatShift(exc.hourend)}`;
      data.shift_start =
        isNaN(exc.hourini) && isNaN(exc.hourini) ? exc.shift_start : start;
      const resException = await createExceptionDay(data);
      if (resException) {
        const asyncExceptionWorkingday = exc.workingDays.map(async (shift) => {
          const toAssignExceptionId = resException.id;
          const formattedIni = formatShift(shift.shift_ini);
          const formattedEnd = formatShift(shift.shift_end);
          const newWorkingDay = {
            shift_string: `${formattedIni}-${formattedEnd}`,
            correlative_id: shift.correlative_id,
            calendarexceptiondayId: toAssignExceptionId
          };
          if (shift.id) {
            newWorkingDay.id = shift.id;
            await workingdayService.update(newWorkingDay);
          } else {
            await workingdayService.create(newWorkingDay);
          }
        });

        await Promise.all(asyncExceptionWorkingday);
      }
    });
  };

  const handleWorkingDays = async (
    toDropWorkingDays,
    toUpdateWorkingDays,
    toCreateWorkingDays
  ) => {
    try {
      // Delete working days
      const asyncDropping = toDropWorkingDays.map(async (workingday) => {
        if (workingday.id) {
          await workingdayService.destroy(workingday.id);
        }
      });
      await Promise.all(asyncDropping);

      // Save new and updated working days
      const asyncMapWorkingDayUpdate = toUpdateWorkingDays.map(
        async (newWorkingDay) => {
          await workingdayService.update(newWorkingDay);
        }
      );
      await Promise.all(asyncMapWorkingDayUpdate);
      const asyncMapWorkingDayCreate = toCreateWorkingDays.map(
        async (newWorkingDay) => {
          await workingdayService.create(newWorkingDay);
        }
      );
      await Promise.all(asyncMapWorkingDayCreate);
    } catch (error) {
      throw error('Error handling working days:', error);
    }
  };

  const separateWorkingDays = (workingDays, calendarId) =>
    workingDays.reduce(
      (acc, shift) => {
        const formattedIni = formatShift(shift.shift_ini);
        const formattedEnd = formatShift(shift.shift_end);
        const newWorkingDay = {
          shift_string: `${formattedIni}-${formattedEnd}`,
          correlative_id: shift.correlative_id,
          calendarId: calendarId
        };

        if (shift.id) {
          newWorkingDay.id = shift.id;
          acc.toUpdateWorkingDays.push(newWorkingDay);
        } else {
          acc.toCreateWorkingDays.push(newWorkingDay);
        }

        return acc;
      },
      { toUpdateWorkingDays: [], toCreateWorkingDays: [] }
    );

  const handleSaveForm = async () => {
    const { calendarSelected, calendarForm } = calendarState;
    try {
      setLoading(true);
      const data = {
        id: calendarSelected.id,
        name: calendarForm.name,
        unique_id: calendarSelected.unique_id,
        working_days: calendarForm.days.map((day) => day | 0).join(','),
        sectorId: projectState.sectorSelected,
        ganttId: currentGantt,
        is_default: calendarForm.is_default
      };
      await calendarService.update(data);

      const { toUpdateWorkingDays, toCreateWorkingDays } = separateWorkingDays(
        calendarForm.workingDays,
        data.id
      );
      await handleWorkingDays(
        toDropWorkingDays,
        toUpdateWorkingDays,
        toCreateWorkingDays
      );

      if (calendarForm.exceptions) {
        await saveExceptions(calendarSelected);
      }

      notificationSystemV2({
        type: 'success',
        key: 'handle-calendar-update-success',
        message: t('calendars_form.title'),
        description: t('calendars_form.update.success')
      });
      const isPrevDefaultCalendar = calendarSelected.is_default;
      const isNewDefaultCalendar = calendarForm.is_default;
      if (!isPrevDefaultCalendar && isNewDefaultCalendar) {
        trackingEvent(
          'select_calendar_as_default',
          {
            ...getBasicAmplitudEventProperties(),
            calendar_name: calendarSelected?.name,
            calendar_id: calendarSelected?.id
          },
          AMPLITUDE_SERVICE
        );
      }
      trackingEvent(
        'calendar_changes_saving',
        {
          ...getBasicAmplitudEventProperties(),
          calendar_name: calendarSelected?.name,
          calendar_id: calendarSelected?.id,
          edit_shift_manually: isShiftsActive
        },
        AMPLITUDE_SERVICE
      );
      activeSectorFlag('update_ponderators_masterplan', true);
      setToDropWorkingDays([]);
      loadCalendars();
      handleCancelEdit();
    } catch (error) {
      notificationSystemV2({
        type: 'error',
        key: 'handle-calendar-update-error',
        message: t('calendars_form.title'),
        description: t('calendars_form.update.error')
      });
    } finally {
      setLoading(false);
      setShowShiftWarningModal(false);
    }
  };

  const handleSubmitForm = async () => {
    const {
      days: weekDays,
      workingDays: shifts,
      exceptions
    } = calendarState.calendarForm;
    const { hoursPerDay } = sector;

    if (hasNoWorkingDaysSelected(weekDays)) {
      trackCalendarEditEvents('no_working_days_selected');
      openNotification({
        title: t('calendars_form.title'),
        description: t('calendars_form.select_day'),
        type: 'error'
      });
      return;
    }
    if (hasNoShifts(shifts)) {
      trackCalendarEditEvents('no_shift_created');
      openNotification({
        title: t('calendars_form.title'),
        description: t('calendars_form.no_shift_was_created'),
        type: 'error'
      });
      return;
    }
    if (hasUndefinedShiftsHours({ weekDays, shifts })) {
      trackCalendarEditEvents('some_shift_is_not_defined');
      openNotification({
        title: t('calendars_form.title'),
        description: t('calendars_form.review_config'),
        type: 'error'
      });
      return;
    }
    if (hasMismatchingWorkingHours({ weekDays, shifts, hoursPerDay })) {
      setShowShiftWarningModal(true);
      return;
    }
    const someExceptionShiftIsInvalid = exceptions.some((el) => {
      const isNew = el.workable && el.hourend && isNaN(el.hourend);
      const isOld =
        el.shift_start === 'null-null' && el.workable && isNaN(el.hourend);
      return isNew || isOld;
    });
    if (someExceptionShiftIsInvalid) {
      openNotification({
        title: t('calendars_form.title'),
        description: t('calendars_form.review_excep'),
        type: 'error'
      });
      return;
    }
    await handleSaveForm();
  };

  const handleWrite = (e) => {
    dispatch(
      calendarActions.setCalendarForm({
        ...calendarState.calendarForm,
        name: e.target.value
      })
    );
  };

  const handleChangeCheckbox = (e) => {
    dispatch(
      calendarActions.setCalendarForm({
        ...calendarState.calendarForm,
        is_default: e.target.checked
      })
    );
  };
  const prevCheckedValue = calendarState?.calendarSelected?.is_default;
  const actualCheckedFormValue = calendarState?.calendarForm?.is_default;
  const lockCheckForm = prevCheckedValue && actualCheckedFormValue;

  return calendarsSelected ? (
    <Fragment>
      <Row className="calendarsEdit div-title">
        <Col span={12}>
          <div className="title">
            <Editable
              text={calendarState.calendarForm.name}
              placeholder="Name"
              childRef={inputRef}
              type="input"
              className="input-table-edit">
              <input
                ref={inputRef}
                type="text"
                name="task"
                className="inputInline-edit"
                placeholder="Name"
                value={calendarState.calendarForm.name}
                onFocus={() => {
                  trackingEvent(
                    'set_calendar_name',
                    {
                      ...getBasicAmplitudEventProperties(),
                      event_source: 'calendar_edition'
                    },
                    AMPLITUDE_SERVICE
                  );
                }}
                onChange={(e) => handleWrite(e)}
                autoComplete="off"
              />
            </Editable>
          </div>
          <div className="subtitle">
            {t('master_plan_calendars.confirure_text')}
          </div>
        </Col>
        <Col span={12}>
          <div className="title">
            {t('master_plan_calendars.confirure_text_default')}
          </div>
          <div className="subtitle">
            {lockCheckForm ? (
              TooltipSystemV2({
                title: t('calendars_form.tooltips.default_calendar_lock'),
                placement: 'right',
                children: (
                  <div>
                    <CheckboxSystem
                      checked={actualCheckedFormValue}
                      disabled={true}
                    />
                  </div>
                )
              })
            ) : (
              <div>
                <CheckboxSystem
                  checked={actualCheckedFormValue}
                  onChange={(e) => handleChangeCheckbox(e)}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row className="calendarsEdit">
        <Col span={24}>
          <div className="title green">
            {t('master_plan_calendars.confirure_text_workin_days')}
          </div>
        </Col>
        <FormDays
          toDropWorkingDays={toDropWorkingDays}
          currentCalendar={currentCalendar}
          calendarsSelected={calendarsSelected}
          setCurrentCalendar={setCurrentCalendar}
          add1Hour={add1Hour}
          setToggleActivation={setIsShiftsActive}
        />
      </Row>
      <Row className="calendarsEdit">
        <Col span={24}>
          <div className="title green">
            {t('master_plan_calendars.confirure_text_exception')}
          </div>
        </Col>
      </Row>
      <Row className="calendarsEdit">
        <Col span={24}>
          <FormExceptionsDays
            toDropWorkingDays={toDropWorkingDays}
            currentCalendar={currentCalendar}
            setCurrentCalendar={setCurrentCalendar}
            add1Hour={add1Hour}
          />
        </Col>
      </Row>
      <Row className="calendarsEdit" style={{ marginBottom: 20 }}>
        <Col span={24}>
          <div className="footerBtns">
            <a
              href="#/"
              onClick={(e) => handleCancelEdit(e)}
              className="refLink skipLink">
              {t('master_plan_calendars.cancel')}
            </a>
            <Button
              disabled={permission === 'V'}
              loading={loading}
              onClick={() => handleSubmitForm()}
              className={
                permission === 'V'
                  ? 'editSubmit disabled'
                  : 'btnSubmit editSubmit'
              }>
              {t('master_plan_calendars.save')}
            </Button>
          </div>
        </Col>
      </Row>
      {showShiftWarningModal && (
        <ShiftWarningModal
          handleShowModal={setShowShiftWarningModal}
          handleSave={handleSaveForm}
        />
      )}
    </Fragment>
  ) : (
    <div className="empty">
      {t('master_plan_calendars.calendar_not_selected')}
    </div>
  );
}

CalendarEdit.propTypes = {
  t: PropTypes.func.isRequired,
  handleCancelEdit: PropTypes.func.isRequired,
  formatShift: PropTypes.func.isRequired,
  add1Hour: PropTypes.func.isRequired,
  loadCalendars: PropTypes.func.isRequired,
  permission: PropTypes.string.isRequired,
  sector: PropTypes.object.isRequired
};

export default withTranslation()(CalendarEdit);
